import { supabase } from "./supabase";
import { readFunctionErrorBody } from "./functionError";
import type { FeatureFlagKey, FeatureVisibility } from "@klubora/shared";

export interface WriteFeatureFlagResponse {
  updated: true;
  flag: {
    id: string;
    key: string;
    visibility: FeatureVisibility;
    params: Record<string, unknown>;
  };
}

/**
 * KLUB-119 (#324). `feature_flags` has only SELECT policies — writes are
 * service-role by design — so the platform goes through the edge function.
 * Empty strings are sent as-is and the function deletes the param, which is how
 * "cleared the field" resolves back to the catalog default instead of a blank
 * notice on someone's phone.
 */
/** What the edge function answers with on a refusal. */
interface WriteFeatureFlagError {
  error?: string;
  field?: string;
  detail?: string;
}

const ERROR_TEXT: Record<string, string> = {
  unknown_feature: "Feature desconhecida.",
  invalid_visibility: "Estado inválido.",
  unknown_param: "Campo de texto desconhecido.",
  must_be_string: "Campo de texto inválido.",
  too_long: "Texto acima do limite permitido.",
  invalid_image_url: "Imagem de fundo inválida.",
  tenant_not_found: "Clube não encontrado.",
  method_not_allowed: "Requisição inválida.",
  unauthorized: "Sessão expirada. Entre novamente.",
  not_platform_admin: "Sua conta não tem acesso ao painel Klubora.",
  insufficient_role: "Seu papel no painel não permite alterar features.",
};

/**
 * `functions.invoke` collapses every non-2xx into a FunctionsHttpError whose
 * message is the constant "Edge Function returned a non-2xx status code" — the
 * response body is only reachable through `error.context`, the raw Response.
 * Without reading it, a too-long title, an unknown key and a real database
 * failure are indistinguishable to the operator.
 */
async function describeInvokeError(error: unknown): Promise<string> {
  const body = await readFunctionErrorBody<WriteFeatureFlagError>(error);
  if (body) {
    const known = body.error ? ERROR_TEXT[body.error] : undefined;
    const field = body.field ? ` (${body.field})` : "";
    if (known) return `${known}${field}`;
    if (body.error) return `${body.error}${field}${body.detail ? `: ${body.detail}` : ""}`;
  }
  return error instanceof Error ? error.message : String(error);
}

export async function writeFeatureFlag(
  tenantId: string,
  key: FeatureFlagKey,
  visibility: FeatureVisibility,
  params: {
    coming_soon_title?: string;
    coming_soon_message?: string;
    coming_soon_image_url?: string;
  } = {},
): Promise<WriteFeatureFlagResponse> {
  const { data, error } = await supabase.functions.invoke<WriteFeatureFlagResponse>(
    "platform-write-feature-flag",
    { body: { tenantId, key, visibility, params } },
  );
  if (error) {
    throw new Error(await describeInvokeError(error));
  }
  if (!data) {
    throw new Error("platform-write-feature-flag returned empty body");
  }
  return data;
}
